<script setup>
import AchievementChart from '@/components/result/ai-literacy-2026/AchievementChart.vue';
import { computed } from 'vue';
import { Radar } from 'vue-chartjs';
import {
  Chart as ChartJS, Title, Tooltip, Legend,
  RadialLinearScale, PointElement, LineElement, Filler
} from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler);

const props = defineProps({  
  viewType: { type: String, default: 'school' },
  principalData: { type: Object, default: null },
  schoolData: { type: Object, default: null },
  classData: { type: Array, default: () => [] },
  pageIndex: { type: Number, default: 0 }
});

// 수준별 비율 계산 함수
const calculateLevelRates = (data) => {
  if (!data) return [];
  
  const totalStudents = data.total_cnt || 0;
  const totalMaleStudents = data.male_cnt || 0;
  const totalFemaleStudents = data.female_cnt || 0;
  
  console.log('👥 calculateLevelRates 데이터:', { totalStudents, totalMaleStudents, totalFemaleStudents });
  
  if (totalStudents === 0) {
    return [
      { level: '4수준', male: 0, female: 0 },
      { level: '3수준', male: 0, female: 0 },
      { level: '2수준', male: 0, female: 0 },
      { level: '1수준', male: 0, female: 0 }
    ];
  }
  
  // 각 수준별 남녀 비율 계산 (0으로 나누기 방지)
  const level4MaleRate = totalMaleStudents > 0 ? ((data.male_level4_cnt || 0) / totalMaleStudents * 100) : 0;
  const level4FemaleRate = totalFemaleStudents > 0 ? ((data.female_level4_cnt || 0) / totalFemaleStudents * 100) : 0;
  const level3MaleRate = totalMaleStudents > 0 ? ((data.male_level3_cnt || 0) / totalMaleStudents * 100) : 0;
  const level3FemaleRate = totalFemaleStudents > 0 ? ((data.female_level3_cnt || 0) / totalFemaleStudents * 100) : 0;
  const level2MaleRate = totalMaleStudents > 0 ? ((data.male_level2_cnt || 0) / totalMaleStudents * 100) : 0;
  const level2FemaleRate = totalFemaleStudents > 0 ? ((data.female_level2_cnt || 0) / totalFemaleStudents * 100) : 0;
  const level1MaleRate = totalMaleStudents > 0 ? ((data.male_level1_cnt || 0) / totalMaleStudents * 100) : 0;
  const level1FemaleRate = totalFemaleStudents > 0 ? ((data.female_level1_cnt || 0) / totalFemaleStudents * 100) : 0;
  //양이사님 요청사항 - 0%인 경우 빈 문자열로 표시
  const result = [
    { level: '4수준', male: level4MaleRate > 0 ? level4MaleRate.toFixed(1) : '', female: level4FemaleRate > 0 ? level4FemaleRate.toFixed(1) : '' },
    { level: '3수준', male: level3MaleRate > 0 ? level3MaleRate.toFixed(1) : '', female: level3FemaleRate > 0 ? level3FemaleRate.toFixed(1) : '' },
    { level: '2수준', male: level2MaleRate > 0 ? level2MaleRate.toFixed(1) : '', female: level2FemaleRate > 0 ? level2FemaleRate.toFixed(1) : '' },
    { level: '1수준', male: level1MaleRate > 0 ? level1MaleRate.toFixed(1) : '', female: level1FemaleRate > 0 ? level1FemaleRate.toFixed(1) : '' }
  ];
  
  console.log('📊 calculateLevelRates 결과:', result);
  return result;
};

// 동적으로 차트 데이터 생성
const displayCharts = computed(() => {
  console.log('🔍 Overview displayCharts computed 실행');
  console.log('📋 props.viewType:', props.viewType);
  console.log('📋 props.principalData:', props.principalData);
  console.log('📋 props.schoolData:', props.schoolData);
  console.log('📋 props.classData:', props.classData);
  
  let charts = [];
  
  if (props.viewType === 'school' && props.principalData) {
    console.log('🏫 학교 전체 차트 생성 시작');
    
    // principalData가 ref인지 직접 객체인지 확인
    const principalDataValue = props.principalData.value || props.principalData;
    console.log('📊 principalDataValue:', principalDataValue);
    
    if (principalDataValue && principalDataValue.classList) {
      // 학교 전체 차트 - 모든 반의 데이터를 합산
      const classList = principalDataValue.classList;
      console.log('📚 classList:', classList);
      
      // 모든 반의 성취수준별 학생수 합산
      const totalSchoolData = classList.reduce((acc, classItem) => {
        return {
          male_level4_cnt: (acc.male_level4_cnt || 0) + (classItem.male_level4_cnt || 0),
          female_level4_cnt: (acc.female_level4_cnt || 0) + (classItem.female_level4_cnt || 0),
          male_level3_cnt: (acc.male_level3_cnt || 0) + (classItem.male_level3_cnt || 0),
          female_level3_cnt: (acc.female_level3_cnt || 0) + (classItem.female_level3_cnt || 0),
          male_level2_cnt: (acc.male_level2_cnt || 0) + (classItem.male_level2_cnt || 0),
          female_level2_cnt: (acc.female_level2_cnt || 0) + (classItem.female_level2_cnt || 0),
          male_level1_cnt: (acc.male_level1_cnt || 0) + (classItem.male_level1_cnt || 0),
          female_level1_cnt: (acc.female_level1_cnt || 0) + (classItem.female_level1_cnt || 0),
          total_cnt: (acc.total_cnt || 0) + (classItem.total_cnt || 0),
          male_cnt: (acc.male_cnt || 0) + (classItem.male_cnt || 0),
          female_cnt: (acc.female_cnt || 0) + (classItem.female_cnt || 0)
        };
      }, {});
      
      console.log('📊 totalSchoolData:', totalSchoolData);
      
      charts = [
        { 
          id: 'school-bar', 
          type: 'bar', 
          label: '성취수준별<br>학생 비율',
          data: calculateLevelRates(totalSchoolData),
          originalData: totalSchoolData
        },
        {
          id: 'school-radar',
          type: 'radar',
          label: '영역별 평균<br>성취율',   
        }
      ];
    } else {
      console.log('❌ principalData 또는 classList가 없음');
    }
  } else if (props.viewType === 'class' && props.classData) {
    console.log('🎓 학급별 차트 생성 시작');
    console.log('📝 classData:', props.classData);
    
    // 학급별 차트 (3반씩)
    charts = props.classData.map((classItem, index) => ({
      id: `class-bar-${index + 1}`,
      type: 'bar',
      label: `${classItem.school_cls}반`,
      data: calculateLevelRates(classItem),
      originalData: classItem
    }));
  } else {
    console.log('❌ 조건에 맞지 않음 - 빈 차트 반환');
  }
  
  console.log('📈 생성된 charts:', charts);
  const result = calculateTotal(charts);
  console.log('✅ 최종 result:', result);
  
  return result;
});

// total 계산
const calculateTotal = (charts) => {
  return charts.map(item => {
    if (item.type === 'bar' && item.data) {
      return {
        ...item,
        data: item.data.map(d => {
          // 원본 데이터에서 각 수준의 실제 학생수를 가져와서 비율 계산
          const levelName = d.level.replace('수준', ''); // '4수준' -> '4'
          const maleCountField = `male_level${levelName}_cnt`;
          const femaleCountField = `female_level${levelName}_cnt`;
          
          // displayCharts에서 사용된 원본 데이터 참조 필요
          // 임시로 male + female 비율의 평균 사용하되, 실제 계산은 다시 해야 함
          let total = 0;
          if (item.originalData) {
            const totalStudents = item.originalData.total_cnt || 0;
            const maleCount = item.originalData[maleCountField] || 0;
            const femaleCount = item.originalData[femaleCountField] || 0;
            //양이사님 요청사항 - 0%인 경우 빈값으로 보이도록 처리
            const totalRate = totalStudents > 0 ? ((maleCount + femaleCount) / totalStudents * 100) : 0;
            total = totalRate > 0 ? totalRate.toFixed(1) : '';
          } else {
            // 원본 데이터가 없는 경우 기존 방식 사용 (임시)
             //양이사님 요청사항 - 0%인 경우 빈값으로 보이도록 처리
            const sumRate = (parseFloat(d.male || 0) + parseFloat(d.female || 0));
            total = sumRate > 0 ? sumRate.toFixed(1) : '';
          }
          
          return {
            ...d,
            total: total
          };
        })
      };
    }
    return item;
  });
};

// 레이더 차트 데이터 동적 생성
const radarData = computed(() => {
  console.log('🎯 radarData computed 실행');
  console.log('📋 props.schoolData:', props.schoolData);
  
  // schoolData가 ref인지 직접 객체인지 확인
  const schoolDataValue = props.schoolData?.value || props.schoolData;
  console.log('📊 schoolDataValue:', schoolDataValue);
  
  if (!schoolDataValue) {
    console.log('❌ schoolData가 없어서 기본 데이터 반환');
    return {
      labels: ['AI·디지털 기술', 'AI·디지털 활용', 'AI·디지털 윤리', '인간중심 AI 설계'],
      datasets: [{
        data: [0, 0, 0, 0],
        borderColor: '#0F7EF1',
        backgroundColor: '#0F7EF110', 
        borderWidth: 2,
        pointRadius: 2,
        pointBackgroundColor: '#0F7EF1',
        fill: true,
      }]
    };
  }

  const radarValues = [
    Number(schoolDataValue.total_field1_rate || 0),
    Number(schoolDataValue.total_field2_rate || 0),
    Number(schoolDataValue.total_field3_rate || 0),
    Number(schoolDataValue.total_field4_rate || 0)
  ];
  
  console.log('📊 radarValues:', radarValues);

  return {
    labels: ['AI·디지털 기술', 'AI·디지털 활용', 'AI·디지털 윤리', '인간중심 AI 설계'],
    datasets: [{
      data: radarValues,
      borderColor: '#0F7EF1',
      backgroundColor: '#0F7EF110', 
      borderWidth: 2,
      pointRadius: 2,
      pointBackgroundColor: '#0F7EF1',
      fill: true,
    }]
  };
});

const radarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: 0 },
  scales: {
    r: {
      min: -25, max: 100,
      ticks: { display: false, stepSize: 25 },
      angleLines: { color: '#efefef', display: true },
      grid: { color: '#efefef', display: true, lineWidth: 2 },     
      pointLabels: { display: false },
    }
  },
  plugins: { legend: { display: false }, tooltip: { display: false, enabled: false} }
};
</script>

<template>
  <div class="card mt-2" :class="`overview-${props.viewType}`">
    <template v-if="props.viewType === 'school'">
        <h5 class="card-title">우리 학교 결과 요약</h5>
    </template>
    <template v-if="props.viewType === 'class'">
        <h5 class="card-title">우리 학교 결과 요약</h5>
        <p class="msg-info pt-2">다음은 학급별 학생 성취수준 비율입니다.</p>
    </template>  
    <div class="table-wrap">
      <div class="ai-table primary mt-2">
        <div class="row thead">
          <div class="th">{{ viewType === 'school' ? '항목' : '학급' }}</div>
          <div class="td">결과 요약</div>
        </div>
        <div class="row" v-for="item in displayCharts" :key="item.id">
          <div class="th" v-html="item.label"></div>
          <div class="td">
            <AchievementChart 
                v-if="item.type === 'bar'"
                :title="item.title" 
                :chart-data="item.data" 
                :view-type="props.viewType"
            />              
            <div v-else-if="item.type === 'radar'" class="radar-area">                  
              <div class="label-layout-wrap">                    
                <p class="lable">AI·디지털 기술</p>
                <n-flex :wrap="false" :size="5" >
                  <p class="lable">인간중심 AI 설계</p>
                  <div class="canvas-wrap">
                    <ul>
                      <li>100%</li>
                      <li>75%</li>
                      <li>50%</li>
                      <li>25%</li>
                      <li>0%</li>
                    </ul>
                    <Radar :data="radarData" :options="radarOptions" />      
                  </div>
                  <p class="lable">AI·디지털 활용</p>
                </n-flex>                    
                <p class="lable">AI·디지털 윤리</p>                   
              </div>   
              <div class="box">
                <n-flex :wrap="false" :size="16" align="center" class="data-row">
                  <p><img src="@/assets/images/result/ai/icon_school.png">학교 평균<br>정답 비율</p>
                  <ul class="achievement-list">
                    <li v-for="(label, index) in radarData.labels" :key="index">
                      <span class="category">{{ label }}</span>
                      <span class="value">{{ 
                        radarData.datasets && 
                        radarData.datasets[0] && 
                        radarData.datasets[0].data && 
                        radarData.datasets[0].data[index] !== undefined 
                          ? Number(radarData.datasets[0].data[index]).toFixed(1) 
                          : '0.0' 
                      }}%</span>
                    </li>
                  </ul>
                </n-flex>            
                <ul class="helper text-[1rem] flex flex-col pt-2">
                  <li><span class="ai-highlight font-bold">학교 평균 정답 비율</span>  :  본 진단검사에 참여한 소속 학교의 해당 학년 학생 전체의 평균 정답 비율을 의미합니다.</li>
                  <li>이 그래프는 하위 영역 간 상대적 수행 능력 차이를 의미하지 않습니다.</li>
                </ul>    
              </div>   
            </div>   
          </div>      
        </div>
      </div>     
    </div>
  </div>
</template>

<style lang="scss" scoped>
[class*='overview'] {
  .radar-area {
    display: flex;  
    align-items: center;
    width: 100%;
    position: relative;
    gap: 1rem;
    .label-layout-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      .n-flex {
          align-items: center; text-align: center;
      }
      .canvas-wrap {
        position: relative;
        width: 230px;
        height: 230px;
        z-index: 1;
        &::after {
          content: "";
          width: 30px;
          height: 30px;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
          background-color: #f2f7fe;
          z-index: -1;
        }
        ul {
          position: absolute;
          right: -65px;
          top: -10px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          line-height: 1.3;
          font-size: 1rem;
          color: #ABAEB9;
          text-align: right;
          z-index: -1;
          font-weight: 600;
          li {
            position: relative;
            &::before {
              content: '';
              position: absolute;
              left: -135px;
              bottom: 50%;
              transform: translateY(-50%);
              width: 130px;
              border-top: 2px dashed #E9E9E9;
            }
          }
        }
      }
      .lable {
        position: relative;
        font-size: 16px;
        font-weight: 600;
        color: var(--result-ai-primary-800);             
      }    
    } 
    .box {
      background-color: var(--result-ai-primary-50);
      border-radius: 10px;
      flex: 1;
      padding: 1rem;
      .data-row {     
        width: 100%;   
        p {
          background-color: var(--smoe-white);
          border-radius: 10px;
          width: 95px;
          height: 95px;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          flex-direction: column;
          font-size: 1.1rem;
          font-weight: 600;
          line-height: 1.2;
          border: 1px solid #004DD1; color: #004DD1; background-color: rgba(0, 77, 209,.2);
        }
        // 아이콘 박스를 줄인 만큼 목록 폭을 넓혀서(칸이 좁아 항목마다 2줄로 줄바꿈되던 문제)
        // 카테고리+수치가 한 줄에 들어가게 한다. 레이더 차트 크기는 건드리지 않는다(건드리면
        // 옆 눈금·점선 위치가 다 같이 틀어진다).
        ul {
          width: calc(100% - 101px);
          li {
            position: relative;
            padding-left: 16px;
            display: flex;
            justify-content: space-between;
            font-size: 1.1rem;
            &::before {
                content: "";
                position: absolute;
                width: 3px;
                height: 3px;
                top: 50%;
                left: 0;
                transform: translateY(-50%);
                border-radius: 100%;
                background-color: var(--result-ai-blak);
            }
            .value {
              font-weight: 700; min-width: 70px; text-align: right; position: relative;
              &::before {
                content: ':';
                position: absolute;
                left: 0;
              }
            }  
          }
        } 
      }    
      .helper {
        li {
          position: relative; padding-left: 24px; text-align: justify;
          &::before {
            content: '';
            position: absolute;
            top: 8px;
            left: 10px;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background-color: var(--result-ai-primary-500);
          }
        }
      }       
    }     
  }
}

.ai-table {
  .row {
    &.thead {
      .th { min-width: auto !important; width: 130px; }
    }
    .th {
      text-align: center;
      font-weight: 600;
    }    
    &:last-child .td { padding: 0rem 0 0rem 1rem; }    
  }
}
</style>